import React, { useState, useEffect } from "react";
import { supabase } from "../../lib/supabase";
import { toast } from "react-hot-toast";
import { BookOpen, Plus, Trash2, Loader2 } from "lucide-react";

export default function BlogSection() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form Fields State
  const [formData, setFormData] = useState({
    title: "",
    category: "Home Building Tips",
    summary: "",
    details: "",
    read_time: "5 min read",
    image: ""
  });

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("blogs")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      setBlogs(data || []);
    } catch (error) {
      toast.error("Failed to load backend logs");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Format current date cleanly (e.g., "June 5, 2026")
    const formattedDate = new Date().toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric"
    });

    try {
      const defaultImg = formData.image.trim() || "https://images.unsplash.com/photo-1541535650810-10d26f5c2ab3?auto=format&fit=crop&w=600&q=80";
      
      const { error } = await supabase.from("blogs").insert([
        {
          title: formData.title,
          category: formData.category,
          summary: formData.summary,
          details: formData.details,
          read_time: formData.read_time,
          image: defaultImg,
          date: formattedDate
        }
      ]);

      if (error) throw error;

      toast.success("Blog published successfully!");
      setFormData({
        title: "",
        category: "Home Building Tips",
        summary: "",
        details: "",
        read_time: "5 min read",
        image: ""
      });
      fetchBlogs();
    } catch (error) {
      toast.error(error.message || "Failed to submit post");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    try {
      const { error } = await supabase.from("blogs").delete().eq("id", id);
      if (error) throw error;
      toast.success("Post removed!");
      fetchBlogs();
    } catch (error) {
      toast.error("Deletion failed");
    }
  };

  return (
    <div className="p-4 lg:p-8 space-y-8">
      {/* Top Welcome Title */}
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 bg-[#0054A6] rounded-xl flex items-center justify-center text-white shadow-md">
          <BookOpen size={24} />
        </div>
        <div>
          <h2 className="text-xl font-black text-slate-950 uppercase tracking-tight">Manage Publications</h2>
          <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Publish news and custom guides dynamically</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 items-start">
        {/* Creation Form Panel */}
        <form onSubmit={handleSubmit} className="bg-white border border-slate-200 p-6 rounded-2xl shadow-sm space-y-4 xl:col-span-1">
          <h3 className="font-black text-sm text-slate-800 uppercase tracking-wider border-b pb-2 flex items-center gap-2">
            <Plus size={18} className="text-[#EE3124]" /> Write New Article
          </h3>
          
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Article Title</label>
            <input required type="text" name="title" value={formData.title} onChange={handleChange} className="w-full text-sm border p-2.5 rounded-lg bg-slate-50 focus:bg-white outline-none focus:ring-2 focus:ring-[#0054A6]" placeholder="e.g., Guide to Bricks" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Category</label>
              <select name="category" value={formData.category} onChange={handleChange} className="w-full text-sm border p-2.5 rounded-lg bg-slate-50 outline-none">
                <option value="Home Building Tips">Home Building Tips</option>
                <option value="Interior Trends">Interior Trends</option>
                <option value="Commercial Updates">Commercial Updates</option>
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Read Time</label>
              <input type="text" name="read_time" value={formData.read_time} onChange={handleChange} className="w-full text-sm border p-2.5 rounded-lg bg-slate-50 placeholder-slate-300" placeholder="5 min read" />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Cover Image Link (Unsplash/URL)</label>
            <input type="url" name="image" value={formData.image} onChange={handleChange} className="w-full text-sm border p-2.5 rounded-lg bg-slate-50 placeholder-slate-300" placeholder="Leave empty for generic blueprint icon" />
          </div>

          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Short Summary (Grid Snippet)</label>
            <textarea required rows={2} name="summary" value={formData.summary} onChange={handleChange} className="w-full text-sm border p-2.5 rounded-lg bg-slate-50 outline-none" placeholder="Brief summary displayed on front preview cards..." />
          </div>

          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Full Detailed Context</label>
            <textarea required rows={4} name="details" value={formData.details} onChange={handleChange} className="w-full text-sm border p-2.5 rounded-lg bg-slate-50 outline-none" placeholder="Deep advisory contents read inside highlighted frames..." />
          </div>

          <button type="submit" disabled={submitting} className="w-full py-3 bg-[#0054A6] hover:bg-[#003c78] disabled:bg-slate-300 text-white font-black text-xs uppercase tracking-widest rounded-xl transition-all shadow-md flex items-center justify-center gap-2">
            {submitting ? <Loader2 className="animate-spin" size={16} /> : "Publish to Live Site"}
          </button>
        </form>

        {/* Live List Display Monitor */}
        <div className="xl:col-span-2 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <h3 className="font-black text-sm text-slate-800 uppercase tracking-wider border-b pb-2 mb-4">
            Live Stream Content Engine ({blogs.length})
          </h3>

          {loading ? (
            <div className="flex justify-center p-12 text-slate-400"><Loader2 className="animate-spin" size={32} /></div>
          ) : blogs.length === 0 ? (
            <p className="text-center py-12 text-sm text-slate-400 font-medium">No published articles yet.</p>
          ) : (
            <div className="space-y-3 max-h-[580px] overflow-y-auto pr-2">
              {blogs.map((post) => (
                <div key={post.id} className="flex items-center justify-between border border-slate-100 bg-slate-50/50 p-3 rounded-xl hover:bg-slate-50 transition-all">
                  <div className="flex items-center gap-4 min-w-0">
                    <img src={post.image} alt="" className="w-12 h-12 rounded-lg object-cover bg-slate-200 shrink-0" />
                    <div className="min-w-0">
                      <h4 className="text-sm font-bold text-slate-900 truncate uppercase tracking-tight">{post.title}</h4>
                      <p className="text-[10px] font-black text-slate-400 uppercase mt-0.5">
                        <span className="text-[#0054A6]">{post.category}</span> • {post.date}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => handleDelete(post.id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors ml-4">
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}